import { EllipsisOutlined, InfoCircleFilled, SearchOutlined } from '@ant-design/icons';
import { UNIT } from '@const/subsystemMetricsResponse.constants';
import { Status } from '@dto/healthStatus.dto';
import { TableCell } from '@dto/pageResponse.dto';
import { DateUtil } from '@dto/util/DateUtil';
import { UnitUtil } from '@dto/util/UnitUtil';
import { TableSummary } from '@parts/table/TableSummary';
import getPathValue from '@rc-component/util/es/utils/get';
import { Button, GetRef, InputRef, Modal, Table, TableProps, Tooltip, TooltipProps } from 'antd';
import { ColumnsType } from "antd/es/table";
import { CompareFn, FilterConfirmProps, FilterValue, SortOrder } from 'antd/es/table/interface';
import { Property } from 'csstype';
import React, { FC, ReactElement, ReactNode, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Highlighter from 'react-highlight-words';
import { BsFilter } from 'react-icons/bs';
import { ColProps, FilterType, Plain, XmColumnChild, XmTableEntry, XmTableProps, XmTableType } from "src/component/table/tableTypes";
import { measureTextWidth, XM_FONT_SIZE, XM_FONT_STACK } from 'src/util/canvasUtils';
import { ColumnMeasurement, headerControlsWidth, layoutColumnWidths, MeasureColumn, measureColumnContent, WidthOptions } from 'src/util/columnWidths';
import { cellBorderPx, cellPaddingPx, MeasureCellItem, measureColumnWidths } from 'src/util/measureColumns';
import { TextUtil } from 'src/util/TextUtil';
import { Log } from '../../service/log.service';
import { renderToMeasureHTML } from '../../util/renderStatic';
import { XmDivAnimated } from '../divAnimated/XmDivAnimated';
import FilterDropDown from './FilterDropDown';
import { getPageRows, getShiftSelection } from './shiftSelection';
import './Table.less';
import { FILL_HEIGHT_CLASS, measureAvailableBodyHeight, measuredRowHeight, resolveCssHeightPx, resolveFillContainer } from './tableHeight';
import VirtualTable from './VirtualTable';

/** Column filter over the cell value; the record is available for `rendered` lookups */
type CellFilter = (rowVal, record?: XmTableEntry) => boolean;

interface VisibleSort<T> {
  compare: CompareFn<T>;
  order: SortOrder;
}

const toVisibleSort = <T,>(sorter: unknown, order: SortOrder | undefined): VisibleSort<T> | null => {
  const compare = typeof sorter === 'function' ? sorter as CompareFn<T>
    : (sorter as { compare?: CompareFn<T>; } | undefined)?.compare;
  return compare && order ? { compare, order } : null;
};

/** The key antd files a column's filter state under (mirrors antd's internal getColumnKey). */
const toAntdColumnKey = (col: { key?: React.Key; dataIndex?: unknown; }): React.Key | undefined => {
  if (col.key != null) return col.key;
  if (Array.isArray(col.dataIndex)) return col.dataIndex.join('.');
  return typeof col.dataIndex === 'string' || typeof col.dataIndex === 'number' ? col.dataIndex : undefined;
};

class XmTableState<T extends XmTableEntry> {
  search: Partial<Record<string, {
    term: React.Key;
    contains: boolean;
    isRegex?: boolean;
    isCaseSensitive?: boolean;
    /** Whole-row predicate, antd's `onFilter` and `onFilteredDataChange` both use it */
    matches: (record: T) => boolean;
  }>> = {};
  extendedProps: XmTableProps<T>;
  scrollY?: number | string;
  hasVerticalScroll?: boolean;
  hasHorizontalOverflow?: boolean;
  /** Measured available width of the wrapper — fed to the engine as parentWidth
   *  (only for tables that opted into a trim-to-fit width mode). */
  containerWidth?: number;
  /** Measured horizontal border of `.ant-table-container`: the table's only width outside its columns. */
  containerFrameWidth?: number;
  /** Measured gutter (px) the vertical scrollbar takes out of the scroll viewport: inside
   *  parentWidth but unavailable to columns, so init() reserves it before the engine runs. */
  vScrollbarWidth?: number;
  /** Virtual table whose h-overflow is only a sub-pixel artifact — adds the
   *  `xm-suppress-hscroll` class so CSS hides the bar. See updateHScrollClamp. */
  hScrollSuppress?: boolean;
}

function shallowEqualProps(a: object | undefined, b: object | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false;
  }
  return true;
}

/** Props comparison for re-init ignores `loading`. */
function withoutLoading<P extends { loading?: unknown; }>({ loading: _loading, ...rest }: P): Omit<P, 'loading'> {
  return rest;
}

function compileRegex(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern);
  } catch {
    Log.warn(`Invalid regular expression: ${pattern}`);
    return null;
  }
}

// Convert width/minWidth values to a number when possible.
function normalizeWidth(value: string | number | undefined): number | string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  const str = String(value).trim();
  if (str.endsWith('em')) return parseFloat(str) * 12;
  if (str.endsWith('px')) return parseFloat(str);
  // percentages, calc(), etc. can't be safely converted without a parent context;
  // leave them as-is (works in plain mode, may misbehave in virtual mode).
  if (str.endsWith('%') || str.includes('(')) return str;
  const n = parseFloat(str);
  return Number.isFinite(n) ? n : str;
}

/**
 * Plain text of a rendered cell, only for PICKING the widest candidates — the winners
 * are then measured from their real HTML (antToMeasureColumns' cellContent), so what
 * this walk misses still reaches the width. Walks the element tree instead of
 * ReactDOMServer because it runs per row. Follows `children` only: text in a prop (a
 * Tooltip/Button title) isn't cell content and must not inflate the column.
 */
function reactText(node: ReactNode, depth = 0): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(n => reactText(n, depth)).join('');
  if (depth >= 8) return ''; // cheap guard against pathologically deep trees
  const children = (node as ReactElement<{ children?: ReactNode; }>)?.props?.children;
  return children == null ? '' : reactText(children, depth + 1);
}

function formatValue<RecordType>(value: (string | number) | (string | number)[], col: ColProps<RecordType>,) {
  if (Array.isArray(value))
    value = value.join(";");

  if (value == null)
    return value;

  if (!col.number)
    return col.trim ? col.trimLength ? TextUtil.trimMiddle(value, col.trimLength) : TextUtil.trimMiddle(value) : value;

  const num = Number(value);
  const fractions = col.decimal ?? UnitUtil.getUnitFractionDigits(col.unit);
  switch (col.unit) {
    case UNIT.timestamp:
      return DateUtil.getDateISO(num * 1000);
    case UNIT.percent:
      return value == null ? NaN.toString() : num.toFixed(fractions);
    case UNIT.millisecond:
    case UNIT.cpu_core:
      return num.toFixed(fractions);
    // case UNIT.io_per_second:
    // case UNIT.IOPS:
    // case UNIT.cpu:
    case undefined:
      return num;
    default:
      return (num / col.magnitude).toFixed(fractions);
  }
}

class IData implements Partial<TableCell> {
  order?: number;
  value?: (string | number)[];
  bgStatus?: Status;
  renderProps?: { noClipboard?: boolean; noTooltip?: boolean; };
  noTooltipColumns?: string[];
}

/** Tooltip with default mouse delay. */
export const TableTooltip: FC<TooltipProps> = (props) => {
  // must keep first value, rerenders on modal close. A lazy useState, not useMemo:
  // the React Compiler turned the memo into a call on every render, and each call
  // scans the whole document — thousands of times per tab switch on graph pages.
  const [zIndex] = useState(() => isInsideModalOrDrawer() ? MODAL_TOOLTIP_ZINDEX : TABLE_TOOLTIP_ZINDEX);
  return <Tooltip mouseLeaveDelay={0.01} mouseEnterDelay={0.5} zIndex={zIndex}
    styles={{ container: { maxWidth: '40vw', maxHeight: '70vh', overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } }}
    {...props}>{props.children}</Tooltip>;
};
const TABLE_TOOLTIP_ZINDEX = 101;
const MODAL_TOOLTIP_ZINDEX = 9999;

const OverflowCellModal: FC<{
  inner: ReactNode;
  content: ReactNode;
  title?: string;
  align?: Property.TextAlign;
}> = ({ inner, content, title, align }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="d-flex-row" style={{ textAlign: align }}>
      <div className="ellipsis-cell">{inner}</div>
      <Button className="xm-cell-modal-btn" size="small" type="text" title="Show full in modal"
        onClick={ev => { ev.preventDefault(); ev.stopPropagation(); setOpen(true); }}>
        <EllipsisOutlined />
      </Button>
      <span onClick={ev => ev.stopPropagation()}>
        <Modal
          title={title
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <InfoCircleFilled style={{ color: 'var(--ant-color-info, #1677ff)', fontSize: '1.4em', flexShrink: 0 }} />{title}
            </span>
            : undefined}
          open={open}
          onOk={() => setOpen(false)}
          onCancel={() => setOpen(false)}
          closable={false}
          footer={(_, { OkBtn }) => <OkBtn />}
          width={416}
          styles={{ header: { marginTop: '1em' } }}
        >
          <div className="xm-cell-modal">{content}</div>
        </Modal>
      </span>
    </div>
  );
};

const MODAL_CLASS = '.ant-modal';
const MODAL_DRAWER_CLASS = '.ant-drawer-open';
function isInsideModalOrDrawer() {
  const modals = document.querySelectorAll(MODAL_CLASS);
  const drawers = document.querySelectorAll(MODAL_DRAWER_CLASS);
  if (!modals?.length && !drawers?.length)
    return false;

  for (const modal of modals) {
    const style = getComputedStyle(modal);
    if (style.display !== 'none')
      return true;
  }

  for (const drawer of drawers) {
    const style = getComputedStyle(drawer);
    if (style.display !== 'none')
      return true;
  }

  return false;
}

/**
 * One XmTableScope for one table
 */
export class XmTableScope<RecordType extends Record<string, any> & { length?: never; }, RecordEntry extends Plain<RecordType> & XmTableEntry = Plain<RecordType> & XmTableEntry> {

  /**
   * Datasource with more rows will render using virtual table
   */
  static readonly ROW_RENDER_LIMIT = 80;

  /**
   * Width for antd's auto-injected rowSelection column. It isn't in `columns`, so the
   * engine never sizes it — left at `auto` the virtual header and body size it
   * independently and every column desyncs.
   */
  static readonly SELECTION_COL_WIDTH = 27;

  /**
   * Max rc-virtual h-scrollbar track−thumb gap (px) treated as spurious. The gap is
   * ≈ 2× the real overflow, so 4 covers a ~2px rounding artifact while genuine
   * overflow (thumb tens of px short) still scrolls. See updateHScrollClamp.
   */
  static readonly HSCROLL_SUPPRESS_MAX_GAP = 4;

  /**
   * Smallest body-height change (px) worth re-rendering for. Applying a height resizes the
   * wrapper, which is itself observed, so committing every sub-pixel read would ping-pong;
   * at a fractional devicePixelRatio the reads drift ~1px on their own.
   *
   * Do NOT raise it to damp a table draining against a content-height container: above
   * PAGE_BOTTOM_GAP the drain stops, but only because the measured value carries no
   * information about the window, so the table freezes on its first value. Fix the container —
   * see the trap in tableHeight.closestVerticalFillContainer.
   */
  static readonly HEIGHT_CHANGE_THRESHOLD = 8;

  /**
   * Debounce for container-driven re-measures (height and width). A gridstack tile or window
   * drag changes size on every frame and each measurement forces a layout, so reads coalesce.
   */
  static readonly MEASURE_DEBOUNCE_MS = 50;

  private tableEl: GetRef<typeof Table>;
  private searchInput: InputRef;
  private data: readonly (RecordEntry)[];
  readonly XmTable: new (props: XmTableProps<RecordType>) => React.Component<XmTableProps<RecordType>, XmTableState<RecordEntry>>;

  constructor(private readonly type: XmTableType = "virtual") {
    this.XmTable = this.createTableClass(type) as unknown as new (props: XmTableProps<RecordType>) => React.Component<XmTableProps<RecordType>, XmTableState<RecordEntry>>;
  }

  private getState: () => Readonly<XmTableState<RecordEntry>>;

  private setState: (
    state:
      | ((
        prevState: Readonly<XmTableState<RecordEntry>>,
        props: Readonly<TableProps<RecordEntry>>
      ) => XmTableState<RecordEntry> | null)
      | (XmTableState<RecordEntry> | null),
    callback?: () => void
  ) => void;

  private getColumnSearchProps(props: ColProps<RecordEntry>): ColProps<RecordEntry> {
    let contains = this.getState().search[getSearchField()]?.contains;

    props.filter.type = props.filter.type ? props.filter.type : 'string';

    const parseNumberFilter = (filter: string) => {
      contains = false;
      if (!filter?.length)
        return () => true;

      const floatRegex = /[+-]?(\d*[.])?\d+/;
      const rangeRegex = new RegExp('^' + floatRegex.source + '-' + floatRegex.source);
      if (/^\D.+/.test(filter)) {
        const value = filter.substring(1);
        const num = Number(value);
        switch (filter.charAt(0)) {
          case '~':
            contains = true;
            return containsFilter(value);
          case '=':
            return (rowVal: number) => rowVal == num;
          case '<':
            return (rowVal: number) => rowVal < num;
          case '>':
            return (rowVal: number) => rowVal > num;
        }
      } else if (rangeRegex.test(filter)) {
        const filterVals = filter.split('-');
        const left = Number(filterVals[0]);
        const right = Number(filterVals[1]);
        return (rowVal: number) => rowVal >= left && rowVal <= right;
      }

      contains = true;
      return containsFilter(filter);
    };

    const getFieldSliced = () => {
      if (props.filter.field[0] === 'data' || props.filter.field[0] === 'rendered')
        return props.filter.field.slice(1);

      return props.filter.field;
    };

    const containsFilter = (filter: string) => (rowVal: number, record: XmTableEntry) => {
      let value: number = getPathValue(record.rendered, getFieldSliced());
      value ??= rowVal;

      return value.toString().includes(filter);
    };

    const matchesRow = (term: string, record: RecordEntry, fce: CellFilter, isRegex: boolean, isCaseSensitive: boolean) => {
      if (record.noFilter) return true;
      if (props.onFilter) return props.onFilter(term, record, isCaseSensitive, isRegex);
      const val = getPathValue(record, props.filter.field);
      return val != null && fce(val, record);
    };

    const createFilterFce = (selectedKeys: string[], type: FilterType, isRegex: boolean, isCaseSensitive: boolean): CellFilter => {
      switch (type) {
        case 'number':
          return parseNumberFilter(selectedKeys[0]);
        case 'timestamp':
          return (rowVal, record) => rowVal >= selectedKeys[0] && rowVal <= selectedKeys[1];
        case 'string': {
          const regex = isRegex ? compileRegex(selectedKeys[0]) : null;
          return (rowVal: string, record: RecordEntry) => {
            let value = rowVal;
            if (record.rendered) {
              const tmp: string = getPathValue(record.rendered, getFieldSliced());
              if (tmp != null)
                value = tmp;
            }

            if (isRegex) {
              if (!regex)
                return false;

              if (props.filter.isTag)
                return value.toString().split(',').some(tag => regex.test(tag));

              return regex.test(value.toString());
            }

            if (isCaseSensitive)
              return value.toString().includes(selectedKeys[0]);

            return value.toString().toLowerCase().includes(selectedKeys[0].toLowerCase());
          };
        }
        default:
          Log.error('Unknown filter type ' + type);
          return () => true;
      }
    };

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex, type: FilterType, isRegex: boolean, isCaseSensitive: boolean) => {
      const state = { ...this.getState() };

      // empty term = reset
      if (type !== 'timestamp' && !selectedKeys[0]) {
        state.search[getSearchField()] = null;
        this.setState(state);
        confirm();
        return;
      }

      const fce = createFilterFce(selectedKeys, type, isRegex, isCaseSensitive);

      state.search[getSearchField()] = {
        term: selectedKeys[0], contains, isRegex, isCaseSensitive,
        matches: record => matchesRow(selectedKeys[0], record, fce, isRegex, isCaseSensitive)
      };
      this.setState(state);
      confirm();
    };

    function getSearchField() {
      return props.filter.field.join();
    }

    const handleReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void) => {
      clearFilters();
      const state = { ...this.getState() };
      state.search[getSearchField()] = null;
      this.setState(state);
      confirm();
    };

    return {
      className: ["xm-search", props.className].join(' ').trim(),
      filterDropdown: dropdownProps =>
        <FilterDropDown
          {...dropdownProps}
          data={this.data}
          filter={props.filter}
          onSearch={handleSearch}
          onReset={handleReset}
          inputRef={node => {
            this.searchInput = node;
          }}
        />,
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value: string, record: RecordEntry) => this.getState().search[getSearchField()]?.matches(record) ?? true,
      filterDropdownProps: {
        onOpenChange: open => {
          if (open)
            setTimeout(() => this.searchInput?.select(), 100);
        }
      },
      render: (text, record: Plain<RecordEntry>, index) => {
        const getText = () => {
          const rendered = getPathValue(record.rendered, getFieldSliced());
          return ((rendered || text) ?? '') + '';
        };

        const field = props.filter.field.join();

        return props.render ? (
          props.render(text, record, index)
        ) : this.getState().search[field] && (props.filter.type === 'string' || this.getState().search[field].contains) ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[this.getState().search[field].term as string]}
            autoEscape
            textToHighlight={getText()}
          />
        ) : (
          text as ReactNode
        );
      },
    };
  }

  virtualActive = false;
  /** Final width per column key, produced by the single width engine. */
  computedWidths: Record<string, number> = {};
  /** Per-column minimum width (stretch floor for tableLayout: auto). */
  computedMinWidths: Record<string, number> = {};
  /** Sum of the computed widths — used as scroll.x to pin a fixed-layout table's
   *  width (else the browser stretches the fixed <col>s to fill and clipping breaks). */
  computedTotalWidth = 0;
  /** Post-mount width corrections keyed by LEAF INDEX (not column key). The offline
   *  engine can't measure render-heavy columns (Popconfirm/Tooltip/Button) and skips
   *  keyless columns entirely (React consumes JSX `key`, so it never reaches props),
   *  so those render narrower in virtual than the plain path. correctVirtualWidths
   *  measures the live cells post-mount and records a wider width here BY INDEX (the
   *  only stable id a keyless column has); init() applies them. Empty for every table
   *  that needs no correction → zero impact on existing tables. Reset on recompute. */
  correctionWidths: Record<number, number> = {};
  /** Content measurement behind `computedWidths`, reused while only the container width changes. */
  private measurement: ColumnMeasurement | null = null;
  private lastMeasureKey: string | null = null;
  private lastLayoutKey: string | null = null;
  /** Gutter the last init() subtracted from parentWidth; the fit var adds it back. */
  reservedGutter = 0;

  getColKey(col: ColProps<RecordEntry>): string {
    if (col.key != null) return String(col.key);
    if (col.dataIndex != null) return ([] as (string | number | symbol)[]).concat(col.dataIndex as never).join('-');
    return '';
  }

  /** Header shows a filter funnel — ours (`filter`) or antd's (`filters` /
   *  `filterDropdown`). The header floor must reserve its ~15px or the trigger
   *  overlaps the title in a narrow column. */
  private hasHeaderFilter(col: ColProps<RecordEntry>): boolean {
    return !!(col.filter || col.filters || col.filterDropdown);
  }

  /** Recursively flatten leaf columns from a columns array (skipping group nodes). */
  flattenLeafCols(cols: readonly ColProps<RecordEntry>[] | undefined): ColProps<RecordEntry>[] {
    if (!cols) return [];
    const result: ColProps<RecordEntry>[] = [];
    for (const col of cols) {
      const children = (col as { children?: readonly ColProps<RecordEntry>[]; }).children;
      if (children?.length) result.push(...this.flattenLeafCols(children));
      else result.push(col);
    }
    return result;
  }

  /** Column props of a JSX <tscope.Col>; React keeps `key` off props, so it is copied from the element. */
  private static jsxColProps<R>(child: XmColumnChild<R>): ColProps<R> {
    return child.key != null ? { ...child.props, key: child.key } : child.props;
  }

  /** Recursively flatten leaf columns from JSX <tscope.Col> children. */
  flattenLeafColsFromJSX(children: XmColumnChild<RecordEntry> | XmColumnChild<RecordEntry>[] | undefined): ColProps<RecordEntry>[] {
    if (!children) return [];
    const arr = Array.isArray(children) ? children : [children];
    const result: ColProps<RecordEntry>[] = [];
    for (const child of arr) {
      if (!child) continue;
      const subChildren = (child.props as { children?: XmColumnChild<RecordEntry>[]; }).children;
      if (subChildren?.length) result.push(...this.flattenLeafColsFromJSX(subChildren));
      else result.push(XmTableScope.jsxColProps(child));
    }
    return result;
  }

  /**
   * A column's raw cell value for a row, across the three data shapes in use: an antd
   * `dataIndex` path, a `name`+`rendered` lookup, and the box/DTO positional
   * `data[colIndex]` array (whose bg-status cells carry no text).
   */
  private getCellValue(row: RecordEntry, col: ColProps<RecordEntry>, colIndex: number): { val: unknown; skip: boolean; } {
    if (col.dataIndex) {
      const path = (Array.isArray(col.dataIndex) ? col.dataIndex : [col.dataIndex]) as (string | number | symbol)[];
      return { val: getPathValue(row, path), skip: false };
    }
    const rendered = (row as { rendered?: Record<string, { value?: unknown; }>; }).rendered;
    if (col.name && rendered) return { val: rendered[col.name]?.value, skip: false };
    const dataArr = (row as { data?: IData[]; }).data;
    if (Array.isArray(dataArr)) {
      const cell = dataArr[colIndex];
      if (cell?.bgStatus) return { val: null, skip: true };
      return { val: cell?.value, skip: false };
    }
    return { val: null, skip: true };
  }

  /**
   * Adapt hand-written ColProps leaves into the engine's {@link MeasureColumn}
   * descriptors, so these tables are sized the same way as the box/DTO ones.
   */
  private antToMeasureColumns(cols: ColProps<RecordEntry>[]): MeasureColumn[] {
    return cols.map((col, colIndex) => {
      const min = normalizeWidth(col.minWidth);
      const max = normalizeWidth(col.maxWidth);
      const explicit = normalizeWidth(col.width);
      const hasNumericWidth = typeof explicit === 'number';

      const cellText = (row: unknown, i: number): string => {
        const { val, skip } = this.getCellValue(row as RecordEntry, col, colIndex);
        // No value source (no dataIndex / `name` / positional data[] — e.g.
        // StorHostCfg's Type column, built from the whole record inside render): the
        // render IS the content. Returning '' left such a column with ZERO candidates,
        // floored to its header, and only the virtual correction widened it — from the
        // painted rows only, so a longer value further down clipped.
        if (skip || val == null) {
          if (typeof col.render !== 'function') return '';
          try {
            const rendered = col.render(val, row as RecordEntry, i);
            // render may return a cell descriptor ({ children, props }), not a node.
            const node = rendered && typeof rendered === 'object' && 'children' in rendered
              ? (rendered as { children?: ReactNode; }).children
              : rendered as ReactNode;
            return reactText(node);
          } catch { return ''; }
        }
        let formatted: unknown = formatValue(val as (string | number) | (string | number)[], col);
        if (formatted == null) return '';
        // Object dataIndex (col.render reads { label, ... } off the value):
        // .toString() would give "[object Object]", so use the render output.
        if (typeof formatted === 'object' && typeof col.render === 'function') {
          try {
            const rendered = col.render(val, row as RecordEntry, i);
            if (typeof rendered === 'string' || typeof rendered === 'number') formatted = rendered;
          } catch { /* keep formatValue result */ }
        }
        const text = String(formatted);
        return text === '[object Object]' ? '' : text;
      };

      // Prefer the column's rendered output so wrappers (icons, badges) are
      // measured. Components needing React context outside render (e.g.
      // react-router <Link>) throw in ReactDOMServer — fall back to plain text.
      const cellContent = typeof col.render === 'function'
        ? (row: unknown, _text: string, i: number) => {
          const { val } = this.getCellValue(row as RecordEntry, col, colIndex);
          try {
            const rendered = col.render(val, row as RecordEntry, i);
            if (rendered != null) return { innerHTML: renderToMeasureHTML(rendered as ReactElement) };
          } catch { /* fall back to plain text */ }
          return {};
        }
        : undefined;

      return {
        key: this.getColKey(col),
        headerContent: col.title as ReactNode,
        hasSort: !!col.sorter,
        hasFilter: this.hasHeaderFilter(col),
        antdFilter: !col.filter && (!!col.filters || !!col.filterDropdown),
        newLines: col.newLines,
        // overflowModal implies trim TOWARDS THE ENGINE so the column gets capped (the
        // "…" button is pointless otherwise). It never shortens text — that's `trim`.
        trim: col.trim || col.overflowModal,
        minWidth: typeof min === 'number' ? min : undefined,
        maxWidth: typeof max === 'number' ? max : undefined,
        // A numeric caller width goes in as `fixedWidth` so the engine RESERVES that
        // space in layoutTotal + the FILL math — otherwise the column is invisible to
        // it, its width leaks on top of parentWidth and the table overflows. It's
        // still never measured or fill-grown, and remapCol keeps the caller's exact
        // value. Non-numeric widths ("auto", "%") are CSS-stretched, so no px can be
        // reserved for them — they stay skipped (e.g. HealthMonitor's width="auto").
        fixedWidth: hasNumericWidth ? explicit : undefined,
        grow: col.grow,
        skip: col.orientation === 'vertical' || !this.getColKey(col)
          || (col.width !== undefined && !hasNumericWidth),
        cellText,
        cellContent,
      } satisfies MeasureColumn;
    });
  }

  /**
   * Compute final column widths via the single engine and cache them by key.
   * Descriptors come from the caller (box/DTO tables pass `measureColumns`) or
   * are derived from the ColProps leaves (hand-written tables).
   */
  computeWidths(cols: ColProps<RecordEntry>[], measureColumns?: MeasureColumn[], widthOptions?: Partial<WidthOptions>): void {
    if (!cols.length) {
      this.computedWidths = {};
      this.computedMinWidths = {};
      this.computedTotalWidth = 0;
      this.measurement = null;
      this.lastMeasureKey = null;
      this.lastLayoutKey = null;
      return;
    }

    // With no rows the engine still measures headers, so every column keeps its
    // header-floor width (prevents empty tables from collapsing to auto width).
    const rows = this.data ?? [];

    const { parentWidth = 0, borderAllowance, ...measureOptions } = widthOptions ?? {};

    // A transient empty render (tab switch, refetch) keeps the last data measurement so
    // columns don't collapse to header floors; layout still re-runs when the container changes.
    const keepMeasurement = rows.length === 0 && Object.keys(this.computedWidths).length > 0;
    const layoutKey = `${parentWidth}|${borderAllowance ?? ''}`;
    if (keepMeasurement && layoutKey === this.lastLayoutKey) return;

    // Skip re-computing when data, columns and options haven't changed.
    // componentDidUpdate re-runs init() on ANY prop reference change, so without this
    // every keystroke in a parent re-triggers renderToString per column. The content
    // measurement is keyed without parentWidth/borderAllowance, so a resize re-runs only the layout step.
    const firstRow = rows[0] as { key?: unknown; };
    const lastRow = rows[rows.length - 1] as { key?: unknown; };
    const measureKey = `${rows.length}|${String(firstRow?.key ?? '')}|${String(lastRow?.key ?? '')}|${cols.map(c => this.getColKey(c)).join(',')}|${JSON.stringify(measureOptions)}`;
    if (measureKey === this.lastMeasureKey && layoutKey === this.lastLayoutKey) return;
    this.lastLayoutKey = layoutKey;

    if (!keepMeasurement && (measureKey !== this.lastMeasureKey || !this.measurement)) {
      this.lastMeasureKey = measureKey;
      // Data/columns changed → any post-mount corrections were measured against the
      // old content; drop them so correctVirtualWidths re-measures from scratch.
      this.correctionWidths = {};
      const descriptors = measureColumns ?? this.antToMeasureColumns(cols);
      this.measurement = measureColumnContent(descriptors, rows, measureOptions);
    }

    const result = layoutColumnWidths(this.measurement, { ...measureOptions, parentWidth, borderAllowance });
    this.computedWidths = result.widths;
    this.computedMinWidths = result.minWidths;
    this.computedTotalWidth = result.totalWidth;
  }

  private remapCol(cp: ColProps<RecordEntry>) {
    let colProps: ColProps<RecordEntry> = { filterIcon: <BsFilter />, ...cp };

    // The engine owns the width ONLY for columns it sized AND that carry no caller
    // width; any caller width (number, "auto", "%", em, or a group width fed back via
    // ColWidthContext) wins. Gate on `cp.width` being unset, not on computedWidths
    // being empty: the memo is keyed on rows/keys/options, NOT width, so a column that
    // switches from width-less to explicit still has its stale natural width cached.
    //
    // Do NOT exclude vertical orientation. Hand-written vertical columns are skipped by
    // the engine, so `computed` is null and they fall through unchanged — but BOX
    // vertical tables DO delegate their width to it, and without the computed value
    // they reach the virtual table width-less and rc-virtual splits it evenly.
    const engineOwnsWidth = cp.width === undefined;
    const computed = engineOwnsWidth ? this.computedWidths[this.getColKey(colProps)] : undefined;
    if (computed != null) {
      colProps.width = computed;
      // minWidth stays the caller's intent / header floor (NOT the grown width),
      // so tableLayout:auto fill tables keep their requested stretch ratios.
      colProps.minWidth = this.computedMinWidths[this.getColKey(colProps)] ?? computed;
    } else {
      // Columns the engine didn't size — vertical, keyless, or not in the leaf set:
      // floor them on their title so they still render at a sensible width.
      if (!colProps.minWidth && cp.orientation !== "vertical") {
        // Prefer the engine's header floor (same hidden-table path scroll.x is summed
        // from) so an explicit-width column bumps to EXACTLY the width the engine
        // reserved — anything else desyncs header from body. Measure locally only for
        // the keyless / no-data columns it never floored.
        const engineFloor = this.computedMinWidths[this.getColKey(colProps)];
        if (engineFloor != null) {
          colProps.minWidth = engineFloor;
        } else {
          const titleWidth = TextUtil.measureText(colProps.title as ReactNode, 'min-content') || 0;
          colProps.minWidth = titleWidth + cellPaddingPx() + cellBorderPx() + headerControlsWidth({
            hasTitle: titleWidth > 0,
            hasSort: !!colProps.sorter,
            hasFilter: this.hasHeaderFilter(colProps),
            antdFilter: !colProps.filter && (!!colProps.filters || !!colProps.filterDropdown),
            gap: this.getState()?.extendedProps?.widthOptions?.controlGap,
          });
        }
      }
      if (colProps.minWidth !== undefined) colProps.minWidth = normalizeWidth(colProps.minWidth);
      if (colProps.width === undefined) colProps.width = colProps.minWidth;
      else colProps.width = normalizeWidth(colProps.width);
      if (typeof colProps.width === 'number' && typeof colProps.minWidth === 'number' && colProps.width < colProps.minWidth)
        colProps.width = colProps.minWidth;
    }

    if (colProps.filter)
      colProps = { ...colProps, ...this.getColumnSearchProps(colProps) };

    if (colProps.render) {
      if (colProps.plainRender === false || (!colProps.plainRender && !this.getState()?.extendedProps?.renderProps?.noClipboard
        && (colProps.sorter || colProps.onFilter))) {
        const oldRender = colProps.render;
        colProps.render = (value, record, index) => {
          if (typeof value === 'number' || cp.number || value == null)
            return this.colRend(value, record, index, oldRender, cp);

          let txt: string;
          if (Array.isArray(value))
            txt = value.join(', ');
          else if (typeof value === 'string')
            txt = value;
          else
            txt = TextUtil.formatJSON(value);

          return (
            <CopyToClipboard
              text={txt}
              onCopy={() => Log.info(`"${txt}" copied to clipboard.`)}>
              <XmDivAnimated>
                {this.colRend(value, record, index, oldRender, cp)}
              </XmDivAnimated>
            </CopyToClipboard>
          );
        };
      } else {
        const oldRender = colProps.render;
        colProps.render = (value, record, index) => this.colRend(value, record, index, oldRender, cp);
      }
    } else {
      colProps.render = (value, record, index) =>
        this.isTooltip(value, cp.noTooltip) ? <TableTooltip title={value}>
          <div style={{ textAlign: this.getAlign(value, cp) }}>{value}</div></TableTooltip> : value as React.ReactNode;
    }

    // when sorted, empty value always at the end
    if (colProps.sorter && typeof colProps.sorter !== 'boolean' && colProps.dataIndex) {
      function sorterNullLast(originalSorter: CompareFn<RecordEntry>, dataIndex?: typeof colProps.dataIndex) {
        return (a: RecordEntry, b: RecordEntry, sortOrder: SortOrder) => {
          let aValue: unknown, bValue: unknown;
          if (typeof dataIndex === 'string' || typeof dataIndex === 'number') {
            aValue = a[dataIndex];
            bValue = b[dataIndex];
          } else {
            const getRootValue = (obj: RecordEntry) => {
              for (const path of dataIndex as []) {
                if (obj[path] == null)
                  return '';

                obj = obj[path];
              }

              return obj;
            };

            aValue = getRootValue(a);
            bValue = getRootValue(b);
          }

          if (aValue === '' && bValue === '' || aValue === null && bValue === null)
            return 0;

          if (aValue === '' || aValue === null)
            return sortOrder === 'ascend' ? 1 : -1;

          if (bValue === '' || bValue === null)
            return sortOrder === 'ascend' ? -1 : 1;

          return originalSorter(a, b, sortOrder);
        };
      }

      if (typeof colProps.sorter === 'function')
        colProps.sorter = sorterNullLast(colProps.sorter, colProps.dataIndex);

      if (typeof colProps.sorter === 'object')
        colProps.sorter = { ...colProps.sorter, compare: sorterNullLast(colProps.sorter.compare, colProps.dataIndex) };
    }

    return colProps;
  }

  private padGroupChildren(parent: ColProps<RecordEntry>, children: ColProps<RecordEntry>[]): ColProps<RecordEntry>[] {
    if (!children?.length || parent.orientation === 'vertical') return children;

    // A nested group contributes the sum of its own children; a leaf its width.
    const widthOf = (c: ColProps<RecordEntry>): number => {
      const kids = c.children;
      if (kids?.length) return kids.reduce((sum, k) => sum + widthOf(k), 0);
      const w = normalizeWidth(c.width);
      return typeof w === 'number' ? w : 0;
    };

    const parentWidth = parent.title == null ? 0 : (measureColumnWidths(
      [{ key: 'p', content: parent.title as ReactNode, newLines: true, fontWeight: 600 }],
      this.getState()?.extendedProps?.widthOptions?.fontScale,
    )['p'] ?? 0);
    const childSum = children.reduce((sum, c) => sum + widthOf(c), 0);
    const deficit = parentWidth - childSum;
    if (deficit <= 0) return children;

    // Distribute the deficit only across flexible value columns — skip helper
    // columns like the color square (className "square-td" / no dataIndex) and
    // nested groups, so those stay at their intended width.
    const isTarget = (c: ColProps<RecordEntry>) =>
      !c.children?.length && c.dataIndex != null && !c.className?.includes('square-td')
      && typeof normalizeWidth(c.width) === 'number';
    const targetCount = children.filter(isTarget).length;
    if (!targetCount) return children;

    const add = Math.ceil(deficit / targetCount);
    return children.map(c => {
      if (!isTarget(c)) return c;
      const w = widthOf(c) + add;
      return { ...c, width: w, minWidth: w };
    });
  }

  private colRend(value: any, record: Plain<RecordEntry>, index: number, render: ColProps<RecordEntry>['render'], cp: ColProps<RecordEntry>) {
    const rendered = render(value, record, index);
    if (rendered?.['type'] === 'meter')
      return rendered as ReactNode;

    // Cells that carry their own tooltip (box renderCol's CellTooltip) must not be
    // wrapped in another one — but they STILL get the overflow "…" treatment below,
    // so this can't early-return before the overflowModal check below (that was the
    // bug where box cells never showed the button).
    const hasOwnTip = !!(rendered as { props?: { tip?: unknown; }; })?.props?.tip;

    let valTip = value;
    if (Array.isArray(value))
      valTip = value.join(', ');
    else if ((typeof value === 'number' || cp.number) && (typeof rendered === 'number' || typeof rendered === 'string'))
      valTip = rendered;

    const align = this.getAlign(value, cp);
    const inner = hasOwnTip || !this.isTooltip(value, cp.noTooltip)
      ? rendered as ReactNode
      : <TableTooltip title={valTip}><div>{rendered as ReactNode}</div></TableTooltip>;

    // Content wider than the column's capped width → ellipsis + a modal with the full
    // value. Styles are the shared .ellipsis-cell / .xm-cell-modal-btn.
    if (cp.overflowModal && this.cellOverflows(value, rendered, cp, record, index)) {
      const modalContent = cp.overflowModalContent ? cp.overflowModalContent(value, record, index) : (rendered as ReactNode);
      return (
        <OverflowCellModal
          inner={inner}
          content={modalContent}
          title={typeof cp.title === 'string' ? cp.title : cp.name}
          align={align}
        />
      );
    }

    // Own-tip cells get no alignment wrapper (they bring their own layout).
    if (hasOwnTip)
      return rendered as ReactNode;

    return (
      <div style={{ textAlign: align }}>
        {inner}
      </div>
    );
  }

  /**
   * True when the cell's text is wider than its column. Text comes from the caller's
   * `overflowText` ('' opts a cell out), else from value/rendered; width from the
   * engine, or the caller's explicit width for columns it skipped.
   */
  private cellOverflows(value: unknown, rendered: unknown, cp: ColProps<RecordEntry>, record: Plain<RecordEntry>, index: number): boolean {
    const measured = this.computedWidths[this.getColKey(cp)];
    const w = measured ?? normalizeWidth(cp.width);
    const colWidth = typeof w === 'number' ? w : undefined;
    if (!colWidth) return false;
    const text = cp.overflowText
      ? cp.overflowText(value, record, index)
      : (typeof rendered === 'string' || typeof rendered === 'number'
        ? String(rendered)
        : Array.isArray(value) ? value.join(', ') : (value == null ? '' : String(value)));
    if (!text) return false;

    const scale = this.getState()?.extendedProps?.widthOptions?.fontScale ?? 1;
    const font = `${XM_FONT_SIZE * scale}px ${XM_FONT_STACK}`;
    // newLines: only the widest line counts
    const lines = cp.newLines ? text.split('\n') : [text];
    const textWidth = Math.max(...lines.map(line => measureTextWidth(TextUtil.collapseWhitespace(line), font)))
      + cellPaddingPx() + cellBorderPx();

    // 2px tolerance: canvas (left) and the hidden-table DOM layout colWidth came from
    // (right) measure the same string a fraction apart. Without it the widest cell of a
    // content-sized column — the one that SET that width — flags itself as overflowing and
    // gets an "…" button.
    return textWidth > colWidth + 2;
  }

  private isTooltip(value: string | number | (string | number)[], noTooltip: boolean = false): boolean {
    if (this.getState()?.extendedProps?.renderProps?.noTooltip)
      return false;

    if (noTooltip)
      return false;

    let val = value;
    if (Array.isArray(value))
      if (value.length === 0)
        return false;
      else if (value.length === 1)
        val = value[0];
      else
        val = value.join(', ');

    return (typeof val === 'string' || typeof val === 'number');
  }

  private getAlign(value: string, cp: ColProps<RecordEntry>): Property.TextAlign {
    switch (cp.filter?.type) {
      case 'timestamp':
        return 'inherit';
      case 'number':
        return 'right';
    }

    if (Array.isArray(value) && value.length === 1)
      value = value[0];
    if (typeof value === 'number' && cp.number !== false)
      return 'right';

    return 'inherit';
  }

  private remapChild(cp: XmColumnChild<RecordEntry>) {
    return { ...cp, props: this.remapCol(XmTableScope.jsxColProps(cp)) };
  }

  updateRow(row: RecordEntry, data: (RecordEntry)[]) {
    return data.map(r =>
      r.key === row.key ?
        row :
        r
    );
  }

  /**
   * Syntactic sugar
   * @param _ Column props with filter shorthand
   */
  readonly Col = (_: ColProps<RecordType>) => {
    return null;
  };

  private createTableClass(type: XmTableType) {

    const outer = this;
    return class XmTableInner extends React.Component<XmTableProps<RecordEntry>, XmTableState<RecordEntry>> {
      constructor(props: XmTableProps<RecordEntry>) {
        super(props);
        outer.getState = () => this.state;
        outer.setState = this.setState.bind(this);
        this.state = new XmTableState<RecordEntry>();
        const extendedProps = this.init();
        this.state = { ...this.state, extendedProps, scrollY: this.computeScrollY(extendedProps) };
      }

      private readonly tableRef = React.createRef<HTMLDivElement>();
      private bodyResizeObserver: ResizeObserver | null = null;
      private observedBody: HTMLElement | null = null;
      private wrapperResizeObserver: ResizeObserver | null = null;
      /** fillHeight only — see observeHeightSources. */
      private heightResizeObserver: ResizeObserver | null = null;
      /** Was the wrapper last seen with zero height (hidden tab pane / collapsed panel)? */
      private wrapperHidden = false;
      private heightDebounceTimer: ReturnType<typeof setTimeout> | null = null;
      /** Did any trigger coalesced into the pending re-measure change the available SIZE? */
      private pendingLatchReset = false;
      /** lastMeasureKey the post-mount correction pass already ran for (so it runs
       *  at most once per distinct data/columns state — see correctVirtualWidths). */
      private correctedForKey: string | null = null;
      /** so the forced-plain warning below is logged once, not on every render */
      private warnedBodyWrapper = false;
      /** Shift+Click anchor: a plain row click sets it, a Shift range keeps it. */
      private selectionAnchor: React.Key | null = null;
      /** Last sort antd reported (null = cleared); undefined until the first change, then defaultSortOrder applies. */
      private sortState: VisibleSort<RecordEntry> | null | undefined = undefined;
      /** Last page antd reported; it resets to 1 on a sort or filter change. */
      private pageState: { current?: number; pageSize?: number; } = {};
      /** Column filters antd last reported; a column it no longer renders is dropped by antd itself. */
      private filterState: Record<string, FilterValue | null> = {};
      private readonly selectedRowCache = new Map<string, RecordEntry>();

      /**
       * Single source of truth for "does this render virtualize" — init() picks
       * tableLayout from it, render() picks which table to mount. They must agree.
       *
       * A caller's `components.body.wrapper` forces PLAIN: only the plain path renders it
       * as a <tbody>, the virtual path hands it to rc-virtual-list as the scroll holder
       * (VirtualTable/BodyGrid.js). So a wrapper that adds per-row state — the config
       * tables put a <Form> around every row — would wrap nothing and every cell reading
       * that context would get undefined. Degrade instead of breaking, and warn once so
       * the mode switch isn't silent. `body.row` / `body.cell` are fine in virtual.
       */
      private useVirtualization(): boolean {
        const virtualizable = type === "virtual" && (
          (this.props.dataSource && this.props.dataSource.length > XmTableScope.ROW_RENDER_LIMIT) ||
          !!this.props.horizontalVirtualization ||
          !!this.props.forceVirtual
        );
        if (!virtualizable) return false;

        const body = this.props.components?.body;
        if (body && typeof body === 'object' && 'wrapper' in body && body.wrapper) {
          if (!this.warnedBodyWrapper) {
            this.warnedBodyWrapper = true;
            Log.error('XmTable: components.body.wrapper is unsupported in the virtual path (it would become the rc-virtual-list holder) — rendering PLAIN instead. Construct XmTableScope with "plain" to make this explicit.');
          }
          return false;
        }
        return true;
      }

      /**
       * Body height, first match wins:
       *   1. explicit `scroll.y` from the caller (any CSS length)
       *   2. `fillHeight` — only the LAST filling table in its container, with room left
       *   3. `visibleRows` rows
       *   4. a `fillHeight` table that declined — one screen at most
       *   5. nothing — the content decides
       */
      private computeScrollY(extProps?: XmTableProps<RecordEntry>): number | string | undefined {
        const virtual = this.useVirtualization();
        const explicit = extProps?.scroll?.y;
        if (typeof explicit === 'number' && explicit > 0) return explicit;

        if (typeof explicit === 'string' && explicit.trim()) {
          if (!virtual) return explicit;
          const px = resolveCssHeightPx(explicit);
          if (px) return px;
        }

        // No DOM in the constructor — the provisional value below stands until
        // componentDidMount measures, which React flushes before the browser paints.
        const measured = this.props.fillHeight && this.tableRef.current
          ? measureAvailableBodyHeight(this.tableRef.current)
          : null;
        if (measured?.height) return measured.height;

        // Not filling: hold `visibleRows`. A plain table that already fits keeps its content height.
        const { visibleRows } = this.props;
        if (visibleRows > 0) {
          if (!virtual && (this.props.dataSource?.length ?? 0) <= visibleRows) return undefined;
          return visibleRows * measuredRowHeight(this.tableRef.current);
        }

        // No visibleRows: one screen at most. A plain table that fits keeps its content height
        // (an unneeded scroll.y leaves an empty scrollbar gutter). A tile has no cap.
        if (measured?.screen) {
          const rows = this.props.dataSource?.length ?? 0;
          if (!virtual && rows * measuredRowHeight(this.tableRef.current) <= measured.screen) return undefined;
          return measured.screen;
        }
        if (measured) return virtual ? measured.viewport : undefined;

        // Asked for nothing, gets nothing: the content decides and whatever it sits in scrolls.
        // A virtual table still needs a NUMBER (a string makes rc-virtual render every row).
        return virtual ? window.innerHeight : undefined;
      }

      /**
       * Commit a recomputed height, ignoring sub-threshold jitter. Guards against a
       * measure → resize → measure cycle: the wrapper necessarily changes height when we
       * apply the new value, and fractional-DPR reads drift by ~1px.
       */
      private readonly updateScrollY = () => {
        const next = this.computeScrollY(this.state?.extendedProps);
        const prev = this.state?.scrollY;
        if (next === prev) return;
        if (typeof next === 'number' && typeof prev === 'number'
          && Math.abs(next - prev) < XmTableScope.HEIGHT_CHANGE_THRESHOLD) return;
        this.setState({ scrollY: next });
      };

      /**
       * Debounced re-measure, trailing edge: every trigger arrives in bursts (drags, renders)
       * and each measurement forces a layout. Never synchronous — measuring straight from
       * `componentDidUpdate` is what React aborts with "Maximum update depth exceeded".
       *
       * `externalChange` means the available SIZE changed, not just that we re-rendered. Only
       * then may the latched overflow flag fall: resetting it on our own render would reopen
       * the feedback loop the latch exists to cut.
       */
      private readonly scheduleScrollYUpdate = (externalChange = false) => {
        this.pendingLatchReset ||= externalChange;
        if (this.heightDebounceTimer !== null) clearTimeout(this.heightDebounceTimer);
        this.heightDebounceTimer = setTimeout(() => {
          this.heightDebounceTimer = null;
          this.updateScrollY();
          if (this.pendingLatchReset) {
            this.pendingLatchReset = false;
            this.resetOverflowLatch();
          }
        }, XmTableScope.MEASURE_DEBOUNCE_MS);
      };

      private readonly handleResizeEvent = () => this.scheduleScrollYUpdate(true);

      // Only trim-to-fit modes measure the container THEMSELVES; full mode (default)
      // leaves the width to the content. A caller-supplied parentWidth always wins — our
      // own wrapper can't see the real space when the caller hugs the table, and full mode
      // needs that width for the fit pin (see useAvailableWidth).
      private usesContainerWidth(): boolean {
        if (this.props.widthOptions?.parentWidth != null) return false;
        const mode = this.props.widthOptions?.mode;
        return mode === 'fill' || mode === 'none';
      }

      // Measure the wrapper's available width and store it; a change re-runs init()
      // (componentDidUpdate) so the engine re-fits the columns to the new width.
      // Only the room left for the columns (paddings off, rounded down): any overshoot widens a
      // content-sized ancestor (modal `min-width: min-content`) and the re-measure loops forever.
      // Computed width, not getBoundingClientRect, which a modal's open animation scales.
      private readonly measureContainer = () => {
        const wrapper = this.tableRef.current;
        if (!wrapper) return;
        const px = (v: string) => parseFloat(v) || 0;
        const horizontalBox = (cs: CSSStyleDeclaration) =>
          px(cs.paddingLeft) + px(cs.paddingRight) + px(cs.borderLeftWidth) + px(cs.borderRightWidth);
        const wrapperStyle = getComputedStyle(wrapper);
        const contentWidth = px(wrapperStyle.width)
          - (wrapperStyle.boxSizing === 'border-box' ? horizontalBox(wrapperStyle) : 0);
        // The inner wrapper's margins stay in: under .text-center it is centred with `margin: 0 auto`
        // (Table.less), whose computed value is the free space beside the table — subtracting it
        // measures the table's own width, so FILL never gets room to grow the capped columns.
        const inner = wrapper.querySelector(':scope > .ant-table-wrapper');
        const w = Math.floor(contentWidth - (inner ? horizontalBox(getComputedStyle(inner)) : 0));
        const container = wrapper.querySelector('.ant-table-container');
        const frame = container ? horizontalBox(getComputedStyle(container)) : undefined;
        if (w > 0 && (w !== this.state.containerWidth || frame !== this.state.containerFrameWidth))
          this.setState({ containerWidth: w, containerFrameWidth: frame });
      };

      private containerDebounceTimer: ReturnType<typeof setTimeout> | null = null;

      // A window drag resizes the wrapper every frame and each re-fit re-renders the whole
      // table, so coalesce the observer callbacks; the trailing call lands on the final width.
      private readonly scheduleContainerMeasure = () => {
        if (this.containerDebounceTimer !== null) clearTimeout(this.containerDebounceTimer);
        this.containerDebounceTimer = setTimeout(() => {
          this.containerDebounceTimer = null;
          this.measureContainer();
        }, XmTableScope.MEASURE_DEBOUNCE_MS);
      };

      // Is rc-virtual's h-scrollbar a sub-pixel rounding artifact (hide it) or real
      // overflow (keep it)? Read it from rc's OWN scrollbar geometry, not from our width
      // sums: at a fractional devicePixelRatio the browser stretches the last column
      // past the width we assign and patchTableMeasurements drifts reads ~1px, so the
      // sums never match what rc laid out. rc sizes the thumb as
      // `(container/range) * container` (getSpinSize), so the thumb-vs-track gap IS the
      // overflow, measured consistently by rc. Thumb nearly filling the track → the
      // range barely exceeds the viewport → artifact. Real overflow leaves it clearly
      // short and keeps scrolling.
      private readonly updateHScrollClamp = () => {
        const setSuppress = (v: boolean) => {
          if (this.state.hScrollSuppress !== v) this.setState({ hScrollSuppress: v });
        };
        if (!outer.virtualActive) return setSuppress(false);
        const bar = this.tableRef.current?.querySelector<HTMLElement>('.ant-table-tbody-virtual-scrollbar-horizontal');
        // No horizontal scrollbar rendered → rc sees no overflow → nothing to hide.
        if (!bar) return setSuppress(false);
        const thumb = bar.querySelector<HTMLElement>('.ant-table-tbody-virtual-scrollbar-thumb');
        const trackW = bar.getBoundingClientRect().width;
        const thumbW = thumb?.getBoundingClientRect().width ?? 0;
        if (trackW <= 0 || thumbW <= 0) return; // not measurable yet — a later pass retries
        // gap ≈ 2× the real overflow px; ~1px artifact → gap ~1-2px. Well under real
        // overflow, which leaves the thumb tens of px shorter than the track.
        setSuppress(trackW - thumbW <= XmTableScope.HSCROLL_SUPPRESS_MAX_GAP);
      };

      // Determines if a table is really overflowing and vertical scroll should be shown
      private readonly checkVerticalScroll = () => {
        // With scroll.y rc-table splits the table into a sticky header + a scrolling
        // `.ant-table-body`; without it there is no `.ant-table-body` at all, just one
        // `.ant-table-content` box ("Unique table" branch). A noScrollY table must be
        // measured on that box or it never learns it overflows, never gets scroll.x, and
        // pushes the page wide instead of scrolling inside.
        //
        // Gated on noScrollY DELIBERATELY: only such a table legitimately has no
        // `.ant-table-body`. Every other one now renders the scroll.y DOM from its very
        // first paint (computeScrollY runs in the constructor) — before that they all
        // transiently rendered this no-scroll DOM, and deciding the overflow from that
        // unsettled paint LATCHED: scroll.x 'max-content' makes the table content-wide,
        // which keeps the overflow true forever.
        const body = this.tableRef.current?.querySelector<HTMLElement>('.ant-table-body');
        // A fillHeight table that declined to fill renders this same no-scroll.y DOM as a
        // settled state, not a transient one, so it has to be measured on `.ant-table-content`
        // too — otherwise it never learns it overflows horizontally and pushes the page wide.
        const noScrollYDom = this.state.extendedProps?.noScrollY || this.state.scrollY === undefined;
        const box = body ?? (noScrollYDom
          ? this.tableRef.current?.querySelector<HTMLElement>('.ant-table-content')
          : null);
        if (!box) {
          if (this.observedBody) {
            this.bodyResizeObserver?.unobserve(this.observedBody);
            this.observedBody = null;
          }
          return;
        }
        // Re-attach observer if the measured element changed (e.g. after re-render)
        if (this.observedBody !== box) {
          if (this.observedBody) this.bodyResizeObserver?.unobserve(this.observedBody);
          this.bodyResizeObserver?.observe(box);
          this.observedBody = box;
        }

        // Nothing laid out yet (hidden tab pane, hard refresh): decide nothing rather than
        // latch a reading taken from zeros below. The observer calls back once there is a box.
        if (box.clientWidth === 0 || box.offsetHeight === 0) return;

        // A vertical scroll only exists in the scroll.y DOM — rc-table gives
        // `.ant-table-content` overflow-y:hidden, so never claim one from it.
        const hasVScroll = body ? body.scrollHeight > body.offsetHeight + 1 : false; // 1px epsilon
        // Add scroll.x (and antd's forced minWidth: 100%) only on a real h-overflow, so a table
        // that fits stays at content width instead of stretching with a gap. scrollWidth counts
        // overflow even where overflow is visible, which is the point — it reads true before
        // any scroll container exists.
        //
        // The epsilon is the VERTICAL SCROLLBAR, not a fixed pixel: it comes out of clientWidth,
        // so a table that fitted exactly "overflows" by precisely its width the instant the body
        // starts scrolling (measured in Firefox, 6px; Chrome's overlay scrollbar takes none).
        // Sub-px gap = rounding (fractional offsetWidth vs integer clientWidth), not a scrollbar.
        const gap = body ? body.offsetWidth - body.clientWidth : 0;
        const vScrollbar = gap >= 1 ? gap : 0;
        const hasHOverflow = box.scrollWidth > box.clientWidth + Math.max(1, vScrollbar);

        // `hasVerticalScroll` LATCHES ON, because it changes the layout it is measured from:
        // `false` adds `.xm-no-vscroll`, which zeroes the header's scrollbar `<col>` and so takes
        // ~97px out of an auto-layout table — narrower columns, more wrapping, taller rows, and
        // the row heights decide the overflow that set the flag. Measured at ~7 flips a second,
        // 200px of content each; hysteresis cannot cover a re-wrap, so the ring is cut instead.
        // The latched state is the honest one — `.xm-no-vscroll` is only a cosmetic trim.
        //
        // `hasHorizontalOverflow` deliberately does NOT latch: its ring only closed through the
        // vertical flag, and latching pinned transient false positives forever (scroll.x brings
        // `min-width: 100%`, the table fills the body, and every later read then sees no
        // overflow to take back).
        const nextVScroll = hasVScroll || this.state.hasVerticalScroll === true;

        // Hand the gutter to the width engine (see init). FILL spends parentWidth to the last
        // pixel once a column is capped, so an unreserved gutter shows up as a permanent
        // h-scrollbar of (gutter − the engine's colCount+1 border allowance).
        //
        // LATCHES UPWARDS, like hasVerticalScroll: reserving it removes the h-scrollbar, which
        // hands the body back that bar's height, which can be what stops the v-scroll — a
        // gutter falling back to 0 would ping-pong. Not cleared by resetOverflowLatch either:
        // it is a property of the browser's scrollbar, not of the data.
        const nextGutter = Math.max(vScrollbar, this.state.vScrollbarWidth ?? 0);

        if (nextVScroll !== this.state.hasVerticalScroll
          || hasHOverflow !== this.state.hasHorizontalOverflow
          || nextGutter !== (this.state.vScrollbarWidth ?? 0))
          this.setState({ hasVerticalScroll: nextVScroll, hasHorizontalOverflow: hasHOverflow, vScrollbarWidth: nextGutter });
      };

      /**
       * Let the latched `hasVerticalScroll` fall again — only where the measurement's INPUTS
       * changed (new props, a window resize), never from our own setState path, which is the
       * loop the latch exists to break. Clears to `undefined`, not `false`: `false` is what adds
       * `.xm-no-vscroll`, so the re-measure would run from the layout the flag itself produces.
       */
      private readonly resetOverflowLatch = () => {
        if (this.state.hasVerticalScroll === undefined) return;
        this.setState({ hasVerticalScroll: undefined }, this.checkVerticalScroll);
      };

      /**
       * Post-mount width correction for VIRTUAL tables only.
       *
       * The engine measures cells by server-rendering `col.render(...)`, and components
       * needing browser/React context (Popconfirm, Tooltip, antd waves, <Link>) throw or
       * under-render there — so those columns get floored to their header and end up
       * narrower than in plain, whose tableLayout:'auto' content-fits for free.
       *
       * Mounted cells DO carry correct HTML. Re-measure their live `innerHTML` through
       * the SAME hidden-table path (identical scale as computedWidths) and WIDEN any
       * column we underestimated. Monotonic and capped by maxWidth → converges after one
       * pass, since a widened column's content width doesn't change.
       */
      private correctionTimer: number | null = null;
      private hScrollTimer: number | null = null;

      /** Poll the h-scroll suppression check on a short ladder. rc-virtual paints its
       *  scrollbar late (VirtualTable's triggerInitialScroll uses setTimeout ~200-300ms),
       *  after the correction ladder has stopped — so a plain post-mount read misses it.
       *  Bounded; updateHScrollClamp only setState's on an actual change. */
      private scheduleHScrollCheck(attempt = 0): void {
        if (!outer.virtualActive) return;
        if (this.hScrollTimer !== null) { window.clearTimeout(this.hScrollTimer); this.hScrollTimer = null; }
        this.updateHScrollClamp();
        if (attempt >= 6) return;
        this.hScrollTimer = window.setTimeout(() => {
          this.hScrollTimer = null;
          this.scheduleHScrollCheck(attempt + 1);
        }, 150 * (attempt + 1)); // 150,300,…,1050ms — spans rc's late paint
      }

      /** Schedule the virtual correction with retries: rc-virtual (VirtualTable)
       *  paints its rows asynchronously (triggerInitialScroll uses setTimeout), so
       *  the body is often still empty on the first frame. Retry on a short ladder
       *  until the rows exist (or give up -> falls back to the engine width). */
      private scheduleVirtualCorrection(attempt = 0): void {
        if (!outer.virtualActive) return;
        if (this.correctionTimer !== null) { window.clearTimeout(this.correctionTimer); this.correctionTimer = null; }
        // Width correction converges once per data/columns state (correctedForKey);
        // the h-scroll viewport measurement runs every pass regardless, so it keeps
        // tracking container resizes and data changes after correction has settled.
        const done = this.correctedForKey === outer.lastMeasureKey ? true : this.correctVirtualWidths();
        this.updateHScrollClamp();
        if (done || attempt >= 10) return;
        this.correctionTimer = window.setTimeout(() => {
          this.correctionTimer = null;
          this.scheduleVirtualCorrection(attempt + 1);
        }, 60 * (attempt + 1));
      }

      private correctVirtualWidths(): boolean {
        if (!outer.virtualActive) return true;
        // Run at most once per distinct data/columns/options state.
        if (this.correctedForKey === outer.lastMeasureKey) return true;
        const wrapper = this.tableRef.current;
        if (!wrapper) return false;

        // Read the ORIGINAL (un-remapped) leaves: remapCol writes the computed width onto
        // every engine-sized column, so extendedProps would make `col.width !== undefined`
        // true everywhere and skip the lot. Only the original props mark the columns the
        // caller pinned. Order is identical, so the positional mapping still holds.
        const leaves = this.props.children
          ? outer.flattenLeafColsFromJSX(this.props.children)
          : outer.flattenLeafCols(this.props.columns);
        if (!leaves.length) return true;
        // Fixed columns reorder the DOM relative to the leaf array, breaking the
        // positional cell→column mapping below — those tables keep the engine width.
        if (leaves.some(l => l.fixed)) return true;
        // Only 'full'/default mode: 'fill'/'none' are trim-to-fit tables whose widths
        // are deliberately distributed to the container — widening would fight that.
        const mode = this.props.widthOptions?.mode;
        if (mode === 'fill' || mode === 'none') return true;
        // Grouped headers break the flat cell<->leaf index mapping — skip (the leaf
        // count would no longer equal the top-level column/child count).
        const rawChildren = this.props.children
          ? (Array.isArray(this.props.children) ? this.props.children : [this.props.children])
          : null;
        const hasGroups = !!this.props.columns?.some(c => (c as ColProps<RecordEntry>).children?.length)
          || !!rawChildren?.some(c => c && ((c.props as { children?: unknown[]; }).children?.length));
        if (hasGroups) return true;

        const rows = wrapper.querySelectorAll<HTMLElement>('.ant-table-tbody .ant-table-row');
        if (!rows.length) return false; // body not rendered yet — retry

        // Selection / expand columns render before the data columns; count them off the
        // front. A negative offset means an unexpected DOM shape (e.g. horizontal
        // virtualization renders only a window of columns) — bail rather than mis-map.
        const cellsPerRow = rows[0].querySelectorAll(':scope > .ant-table-cell').length;
        const offset = cellsPerRow - leaves.length;
        // We only ever measure now — mark this state handled regardless of outcome.
        this.correctedForKey = outer.lastMeasureKey;
        if (offset < 0) return true;

        const items: MeasureCellItem[] = [];
        const currentByIndex: Record<number, number> = {};
        const maxRows = Math.min(rows.length, 60); // bound the work on tall viewports
        for (let ri = 0; ri < maxRows; ri++) {
          const cells = rows[ri].querySelectorAll<HTMLElement>(':scope > .ant-table-cell');
          cells.forEach((cell, ci) => {
            const li = ci - offset;
            if (li < 0 || li >= leaves.length) return;
            const col = leaves[li];
            // Never override a caller width nor a deliberately capped (trim) column.
            // Index, not key, is the id: React consumes a JSX `key`, so render-heavy
            // columns are usually keyless and invisible to the engine.
            if (col.width !== undefined || col.trim || col.overflowModal) return;
            const html = cell.innerHTML;
            if (!html) return;
            if (currentByIndex[li] == null) currentByIndex[li] = cell.getBoundingClientRect().width;
            items.push({ key: `${li} ${ri}`, content: null, innerHTML: html, newLines: col.newLines });
          });
        }
        if (!items.length) return true;

        // Natural (content-fit) width per column INDEX = max over its measured cells.
        const measured = measureColumnWidths(items, this.props.widthOptions?.fontScale);
        const naturalByIndex: Record<number, number> = {};
        for (const k of Object.keys(measured)) {
          const i = Number(k.slice(0, k.indexOf(' ')));
          naturalByIndex[i] = Math.max(naturalByIndex[i] ?? 0, measured[k]);
        }

        // Record a correction where the natural content is wider than what's rendered
        // now (capped by maxWidth). Monotonic — never shrinks — so it converges: once
        // applied the column renders at its natural width and the next measure agrees.
        let changed = false;
        for (const idxStr of Object.keys(naturalByIndex)) {
          const i = Number(idxStr);
          const col = leaves[i];
          const max = normalizeWidth(col.maxWidth);
          const natural = naturalByIndex[i];
          const target = typeof max === 'number' ? Math.min(natural, max) : natural;
          const current = currentByIndex[i] ?? 0;
          if (target > current + 2 && target > (outer.correctionWidths[i] ?? 0) + 1) {
            outer.correctionWidths[i] = target;
            changed = true;
          }
        }
        if (!changed) return true;

        // init() applies correctionWidths by index onto the rendered columns and
        // recomputes the total (virtual scroll.x); the computeWidths memo is unchanged
        // so no offline re-measure runs.
        this.setState({ extendedProps: this.init() });
        return true;
      }

      private init(): XmTableProps<RecordEntry> {
        const extProps: XmTableProps<RecordEntry> = { renderProps: { noClipboard: true, noTooltipColumns: [] }, ...this.props };
        // Always set explicitly: antd would switch to 'fixed' on its own once `scroll` is
        // set, changing every table's layout. Default 'auto' — the caller's `width` is
        // then only honoured while it's ≥ the content (see tableLayout in tableTypes).
        //
        // 'fixed' for trim-to-fit ('fill'/'none'): under 'auto' the <col width> is a hint
        // and the cell's white-space:nowrap lets the browser grow the column to fit, so a
        // capped column never actually clips — yet the "…→modal" button, measured against
        // the capped engine width, still appears.
        const trimToFit = this.props.widthOptions?.mode === 'fill' || this.props.widthOptions?.mode === 'none';
        // 'fixed' for virtual: rc-virtual always lays the BODY out by explicit widths, but
        // the header is a normal antd table. Under 'auto' it content-sizes independently,
        // so a header wider than its body cell (a `filters` funnel next to the title)
        // drifts the two apart.
        const willVirtualize = this.useVirtualization();
        extProps.tableLayout = this.props.tableLayout ?? (trimToFit || willVirtualize ? 'fixed' : 'auto');
        outer.data = this.props.dataSource;
        outer.virtualActive = willVirtualize;
        // `stretch` spans the table to 100% of its container (vs the default min-content
        // hug) — pure CSS, orthogonal to the engine's `mode: 'fill'`. Turned into a class
        // so callers pass a typed prop instead of a magic className, then deleted so it
        // never reaches antd Table as an unknown DOM attribute.
        extProps.className = ["xm-table", this.props.stretch ? 'xm-table-stretch' : null, extProps.className].filter(Boolean).join(' ');
        delete extProps.stretch;

        // The rowSelection checkbox column isn't in `columns`, so it never gets a measured
        // width and stays `auto` — which desyncs the virtual header from the body.
        if (extProps.rowSelection && typeof extProps.rowSelection === 'object' && extProps.rowSelection.columnWidth === undefined)
          extProps.rowSelection = { ...extProps.rowSelection, columnWidth: XmTableScope.SELECTION_COL_WIDTH };

        // Both paths need the engine: plain tables also have to grow columns past their
        // header floor, or long values overflow into the next column.
        {
          const leaves = extProps.children
            ? outer.flattenLeafColsFromJSX(extProps.children)
            : outer.flattenLeafCols(extProps.columns);
          // Only trim-to-fit modes need the container width, so full mode keeps
          // widthOptions untouched — no measurement, no extra recompute.
          let widthOptions = extProps.widthOptions;
          outer.reservedGutter = 0;
          if (widthOptions && (widthOptions.mode === 'fill' || widthOptions.mode === 'none')) {
            const avail = widthOptions.parentWidth ?? this.state?.containerWidth ?? 0;
            // The width the COLUMNS can occupy, not the container's: the scrollbar gutter is
            // inside the container but outside the viewport. Measured, never assumed — this app
            // styles its scrollbars, so the gutter is neither a platform default nor integral.
            outer.reservedGutter = this.state?.vScrollbarWidth ?? 0;
            widthOptions = { ...widthOptions, parentWidth: Math.max(0, avail - outer.reservedGutter) };
            // The measured frame instead of the engine's estimate, which leaves a gap at the right edge.
            if (this.props.widthOptions?.parentWidth == null && this.state?.containerFrameWidth != null)
              widthOptions = { ...widthOptions, borderAllowance: this.state.containerFrameWidth };
          }
          outer.computeWidths(leaves, extProps.measureColumns, widthOptions);
        }

        if (extProps.children) {
          if (!Array.isArray(extProps.children))
            extProps.children = [extProps.children];

          extProps.children = extProps.children.filter(c => c).map(child => {
            if (child.props.children) {
              const kids = child.props.children as XmColumnChild<RecordEntry>[];
              const mapped = outer.padGroupChildren(child.props, kids.map((c) => outer.remapCol(c.props)));
              return {
                ...child,
                props: { ...child.props, children: kids.map((c, i) => ({ ...c, props: mapped[i] })) },
              };
            } else {
              return outer.remapChild(child);
            }
          });
        } else if (extProps.columns) {
          extProps.columns = extProps.columns.map(child => {
            if (child.children) {
              const mapped = child.children.map((c: ColProps<RecordEntry>) => outer.remapCol(c));
              return {
                ...child,
                children: outer.padGroupChildren(child, mapped)
              };
            } else {
              return outer.remapCol(child);
            }
          });
        }

        // Apply post-mount virtual corrections (keyed by LEAF INDEX; only flat full-mode
        // virtual tables record any, so a flat leaf walk maps 1:1). The total is
        // recomputed afterwards so virtual scroll.x spans the widened columns — the
        // engine's own total omitted the keyless ones entirely.
        if (Object.keys(outer.correctionWidths).length) {
          const outLeaves = extProps.children
            ? outer.flattenLeafColsFromJSX(extProps.children)
            : outer.flattenLeafCols(extProps.columns);
          let total = 0;
          outLeaves.forEach((leaf, i) => {
            const corr = outer.correctionWidths[i];
            if (corr != null) {
              // Clamp so a correction only ever WIDENS: it's measured against the
              // *rendered* width, which can be transiently narrow mid-transition (a
              // columns/tab switch), and overwriting blindly then collapsed the column.
              const base = normalizeWidth(leaf.width);
              const next = typeof base === 'number' ? Math.max(corr, base) : corr;
              leaf.width = next;
              if (leaf.minWidth == null) leaf.minWidth = next;
            }
            const w = normalizeWidth(leaf.width) ?? outer.computedWidths[outer.getColKey(leaf)] ?? normalizeWidth(leaf.minWidth);
            if (typeof w === 'number') total += w;
          });
          if (extProps.rowSelection && typeof extProps.rowSelection === 'object')
            total += Number(extProps.rowSelection.columnWidth ?? XmTableScope.SELECTION_COL_WIDTH) || 0;
          if (total > 0) outer.computedTotalWidth = total;
        }

        this.wireShiftSelection(extProps);
        return extProps;
      }

      /** Controlled checkbox selection gets a Shift+Click range over the visible rows; antd's native range is ignored. */
      private wireShiftSelection(extProps: XmTableProps<RecordEntry>): void {
        const selection = extProps.rowSelection;
        if (!selection || typeof selection !== 'object' || selection.type === 'radio') return;
        const { selectedRowKeys, onChange } = selection;
        if (!selectedRowKeys || !onChange) return;
        if (selection.preserveSelectedRowKeys) this.cacheSelectedRows(extProps.dataSource ?? [], selectedRowKeys);
        extProps.rowSelection = {
          ...selection,
          onChange: (keys, rows, info) => {
            if (info?.type !== 'multiple') onChange(keys, rows, info);
          },
        };
        const ownOnRow = extProps.onRow;
        extProps.onRow = (record, index) => {
          const rowProps = ownOnRow?.(record, index) ?? {};
          return {
            ...rowProps,
            onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
              this.handleSelectionClick(record, event);
              rowProps.onClickCapture?.(event);
            },
          };
        };
        const ownOnChange = extProps.onChange;
        extProps.onChange = (pagination, filters, sorter, extra) => {
          const primary = Array.isArray(sorter) ? sorter[0] : sorter;
          this.sortState = toVisibleSort<RecordEntry>(primary?.column?.sorter, primary?.order ?? undefined);
          this.pageState = { current: pagination.current, pageSize: pagination.pageSize };
          this.filterState = filters;
          ownOnChange?.(pagination, filters, sorter, extra);
        };
      }

      /** Keeps the records of selected keys that leave dataSource, as antd does for preserveSelectedRowKeys. */
      private cacheSelectedRows(rows: readonly RecordEntry[], selectedRowKeys: readonly React.Key[]): void {
        const selected = new Set(selectedRowKeys.map(String));
        rows.forEach((row) => {
          const key = String(this.getRowKey(row));
          if (selected.has(key)) this.selectedRowCache.set(key, row);
        });
        [...this.selectedRowCache.keys()].forEach((key) => {
          if (!selected.has(key)) this.selectedRowCache.delete(key);
        });
      }

      private getRowKey(record: RecordEntry): React.Key {
        const { rowKey } = this.props;
        if (typeof rowKey === 'function') return rowKey(record);
        return record[rowKey ?? 'key'];
      }

      private handleSelectionClick(record: RecordEntry, event: React.MouseEvent<HTMLElement>): void {
        // React bubbles portal clicks (a Popconfirm opened from a cell) through the row
        if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target)) return;
        // Shift+Click elsewhere in the row keeps its browser meaning (a link opens a new window)
        if (!event.target.closest('.ant-table-selection-column')) return;
        const targetKey = this.getRowKey(record);
        if (!event.shiftKey) {
          this.selectionAnchor = targetKey;
          return;
        }
        const selection = this.props.rowSelection;
        const isSelectable = (row: RecordEntry | undefined): boolean => !!row && !selection.getCheckboxProps?.(row)?.disabled;
        if (!isSelectable(record)) return;
        // capture phase: the checkbox never sees the click, so antd's own Shift range never runs
        event.stopPropagation();
        // a cancelled native toggle leaves the input's DOM state out of sync with the controlled one
        if (!(event.target instanceof HTMLInputElement)) event.preventDefault();
        const rowByKey = new Map(this.selectedRowCache);
        (this.props.dataSource ?? []).forEach((row) => rowByKey.set(String(this.getRowKey(row)), row));
        const { selectedKeys, anchorKey } = getShiftSelection({
          orderedKeys: this.getVisibleRows().map((row) => this.getRowKey(row)),
          selectedKeys: selection.selectedRowKeys ?? [],
          anchorKey: this.selectionAnchor,
          targetKey,
          isSelectable: (key) => isSelectable(rowByKey.get(String(key))),
        });
        this.selectionAnchor = anchorKey;
        const rows = selectedKeys.map((key) => rowByKey.get(String(key))).filter((row): row is RecordEntry => !!row);
        selection.onChange?.(selectedKeys, rows, { type: 'multiple' });
      }

      /** Rows of the current page in display order: the column searches antd applies, its sort, then the page. */
      private getVisibleRows(): RecordEntry[] {
        const applied = this.getAppliedSearches();
        const rows = (this.props.dataSource ?? []).filter((row) => applied.every((matches) => matches(row)));
        const sort = this.getVisibleSort();
        // antd negates the compare result for 'descend'; the null-last sorters rely on it
        if (sort) rows.sort((a, b) => (sort.order === 'ascend' ? 1 : -1) * sort.compare(a, b, sort.order));
        return this.getCurrentPage(rows);
      }

      private getCurrentPage(rows: RecordEntry[]): RecordEntry[] {
        const { pagination } = this.props;
        if (!pagination) return rows;
        return getPageRows({
          rows,
          current: pagination.current ?? this.pageState.current ?? pagination.defaultCurrent ?? 1,
          pageSize: pagination.pageSize ?? this.pageState.pageSize ?? pagination.defaultPageSize ?? 10,
        });
      }

      /** Searches of the rendered columns antd reports as filtered; a column without key or dataIndex keeps its search. */
      private getAppliedSearches(): ((row: RecordEntry) => boolean)[] {
        return this.getLeafColumns()
          .filter((col) => {
            const key = toAntdColumnKey(col);
            return key == null || !!this.filterState[String(key)]?.length;
          })
          .map((col) => (col.filter ? this.state.search[col.filter.field.join()]?.matches : undefined))
          .filter((matches): matches is (row: RecordEntry) => boolean => !!matches);
      }

      private getLeafColumns(): ColProps<RecordEntry>[] {
        const ep = this.state.extendedProps;
        return ep.children ? outer.flattenLeafColsFromJSX(ep.children) : outer.flattenLeafCols(ep.columns);
      }

      private getVisibleSort(): VisibleSort<RecordEntry> | null {
        const leaves = this.getLeafColumns();
        const controlled = leaves.find((col) => col.sortOrder);
        if (controlled) return toVisibleSort<RecordEntry>(controlled.sorter, controlled.sortOrder);
        if (this.sortState !== undefined) return this.sortState;
        const byDefault = leaves.find((col) => col.defaultSortOrder);
        return byDefault ? toVisibleSort<RecordEntry>(byDefault.sorter, byDefault.defaultSortOrder) : null;
      }

      shouldComponentUpdate(nextProps: XmTableProps<RecordEntry>, nextState: XmTableState<RecordEntry>): boolean {
        return this.state !== nextState || !shallowEqualProps(this.props, nextProps);
      }

      // Report final per-leaf-column widths (column order) so a parent group can
      // sync widths across stacked tables. Runs after render (widths are final).
      private reportWidths(): void {
        const cb = this.props.onWidthsComputed;
        if (!cb) return;
        const ep = this.state.extendedProps;
        const leaves = ep.children
          ? outer.flattenLeafColsFromJSX(ep.children)
          : outer.flattenLeafCols(ep.columns);
        const widths = leaves.map(l => {
          const w = normalizeWidth(l.width);
          return typeof w === 'number' ? w : 0;
        });
        cb(widths);
      }

      componentDidMount(): void {
        window.addEventListener('resize', this.handleResizeEvent);
        this.bodyResizeObserver = new ResizeObserver(() => this.checkVerticalScroll());
        requestAnimationFrame(() => this.checkVerticalScroll());
        // Observe the wrapper for fill/none tables (re-fit to the container) AND virtual
        // ones (re-measure the holder for h-scroll suppression — rc-virtual settles its
        // layout late, after the correction ladder has stopped).
        if ((this.usesContainerWidth() || type === 'virtual') && this.tableRef.current) {
          if (this.usesContainerWidth()) {
            this.measureContainer();
            // On a hard refresh / route change the layout isn't settled in didMount, so
            // offsetWidth reads 0 or a transient value — re-measure next frame.
            requestAnimationFrame(() => this.measureContainer());
          }
          this.wrapperResizeObserver = new ResizeObserver(() => {
            if (this.usesContainerWidth()) this.scheduleContainerMeasure();
            this.updateHScrollClamp();
          });
          this.wrapperResizeObserver.observe(this.tableRef.current);
        }
        this.observeHeightSources();
        // Replace the constructor's provisional height with the measured one. A setState in
        // componentDidMount is flushed before the browser paints, so the provisional value
        // never reaches the screen — no visible jump.
        this.updateScrollY();
        this.reportWidths();
        // Correct render-heavy virtual columns once rc-virtual has painted its rows
        // (retries because VirtualTable paints the body asynchronously).
        this.scheduleVirtualCorrection();
        // Poll for rc's late-painted horizontal scrollbar to decide suppression.
        this.scheduleHScrollCheck();
      }

      /**
       * Re-measure when the CONTAINER resizes — it can change without a window resize (a
       * collapsing sidebar, a tab switch, a filter bar rewrapping).
       *
       * The wrapper is observed too but its own resizes are IGNORED: its height is what we set,
       * so reacting would close the loop. The one exception is the zero-height→visible edge — a
       * table mounted in a hidden tab pane measures nothing and would never re-measure.
       */
      private observeHeightSources(): void {
        const wrapper = this.tableRef.current;
        if (!this.props.fillHeight || !wrapper) return;
        this.wrapperHidden = wrapper.offsetHeight === 0;
        this.heightResizeObserver = new ResizeObserver(entries => {
          const hidden = wrapper.offsetHeight === 0;
          const revealed = this.wrapperHidden && !hidden;
          this.wrapperHidden = hidden;
          if (!revealed && !entries.some(e => e.target !== wrapper)) return;
          this.scheduleScrollYUpdate(true); // external: the container really did change size
        });
        const container = resolveFillContainer(wrapper)?.el;
        if (container) this.heightResizeObserver.observe(container);
        this.heightResizeObserver.observe(wrapper);
      }

      componentDidUpdate(prevProps: XmTableProps<RecordEntry>, prevState: XmTableState<RecordEntry>) {
        // Re-init on prop changes AND when the measured container width changed
        // (fill/none tables re-fit on resize). Without the state check a resize
        // updated containerWidth but never recomputed the widths.
        const propsChanged = !shallowEqualProps(withoutLoading(prevProps), withoutLoading(this.props));
        // vScrollbarWidth is state too — without this the measured gutter never reaches the
        // engine (the same trap containerWidth hit).
        if (propsChanged || prevState.containerWidth !== this.state.containerWidth
          || prevState.containerFrameWidth !== this.state.containerFrameWidth
          || prevState.vScrollbarWidth !== this.state.vScrollbarWidth)
          this.setState({ extendedProps: this.init() });
        // New props/data are a real change of input, so drop the overflow latch and let the
        // measurement below decide again. Deliberately NOT on a state-only update: our own
        // setState is the feedback path the latch exists to cut.
        if (propsChanged) this.resetOverflowLatch();
        if (prevProps.dataSource !== this.props.dataSource) this.reportFilteredData();
        // After paint, never synchronously: `hasHorizontalOverflow` does not latch, so measuring
        // from here would apply scroll.x, re-measure the changed layout and flip back — "Maximum
        // update depth exceeded". The rAF also lets rc-table settle its column widths.
        requestAnimationFrame(() => this.checkVerticalScroll());
        // Re-measure on any re-render: what sits above and below the table can change
        // without the window resizing (filters rewrapping, a summary row appearing), and
        // new data can cross the visibleRows count or change the row height.
        // Scheduled, never synchronous — see scheduleScrollYUpdate.
        if (this.props.fillHeight || this.props.visibleRows) this.scheduleScrollYUpdate();
        this.reportWidths();
        // Re-check after any re-render (new data → new lastMeasureKey → re-corrects;
        // guarded by correctedForKey so an unchanged table doesn't re-measure).
        this.scheduleVirtualCorrection();
        // On a data/columns change rc repaints its scrollbar late again — re-poll.
        if (propsChanged) this.scheduleHScrollCheck();
      }

      /** antd re-filters a new dataSource silently; tells `onFilteredDataChange` which rows survived */
      private reportFilteredData() {
        if (!this.props.onFilteredDataChange || !this.props.dataSource) return;
        const active = Object.values(this.state.search).filter(Boolean);
        if (!active.length) return;
        this.props.onFilteredDataChange(this.props.dataSource.filter(row => active.every(entry => entry.matches(row))));
      }

      componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleResizeEvent);
        if (this.observedBody) this.bodyResizeObserver?.unobserve(this.observedBody);
        this.bodyResizeObserver?.disconnect();
        this.bodyResizeObserver = null;
        this.observedBody = null;
        this.wrapperResizeObserver?.disconnect();
        this.wrapperResizeObserver = null;
        this.heightResizeObserver?.disconnect();
        this.heightResizeObserver = null;
        if (this.heightDebounceTimer !== null) { clearTimeout(this.heightDebounceTimer); this.heightDebounceTimer = null; }
        if (this.containerDebounceTimer !== null) { clearTimeout(this.containerDebounceTimer); this.containerDebounceTimer = null; }
        if (this.correctionTimer !== null) { window.clearTimeout(this.correctionTimer); this.correctionTimer = null; }
        if (this.hScrollTimer !== null) { window.clearTimeout(this.hScrollTimer); this.hScrollTimer = null; }
      }
      render() {
        const useVirtualization = this.useVirtualization();

        const wrapperClass = [
          'xm-table-wrapper',
          // Lets a filling table above this one see it and decline — see measureAvailableBodyHeight.
          this.props.fillHeight ? FILL_HEIGHT_CLASS : '',
          this.state.hasVerticalScroll === false ? 'xm-no-vscroll' : '',
          useVirtualization && this.state.hScrollSuppress ? 'xm-suppress-hscroll' : '',
        ].filter(Boolean).join(' ');

        // An engine-sized fixed-layout table must pin its width to the SUM of the computed
        // column widths. Otherwise antd renders at width:100% / max-content and the
        // browser stretches the fixed <col>s to fill, so a column capped below its content
        // is drawn wider than computed and never clips — while the "…" button, measured
        // against the computed width, still appears.
        //
        // Applies to BOTH paths: rc-virtual honours 'fixed' as long as scroll.x is numeric
        // AND every column has a numeric width, which the engine guarantees for
        // trim-to-fit tables. Without it a fixed body divides the width equally ("all
        // columns equal").
        const mode = this.props.widthOptions?.mode;
        const engineFixedScrollX = this.state.extendedProps.tableLayout === 'fixed'
          && (mode === 'fill' || mode === 'none') && outer.computedTotalWidth > 0
          ? outer.computedTotalWidth
          : undefined;

        // Bordered-virtual right-clip fix: antd sizes the scroll viewport (container /
        // sticky header / virtual holder) ~colCount+1 px NARROWER than the sum of the
        // fixed <col> widths, so the last column clips on the right with no scroll.
        // Publish the engine total as a CSS var and force those viewports to it (see
        // .xm-table-fitwidth in _table.less) so viewport == columns.
        //
        // ONLY when the table fits its container (total <= parentWidth): a table whose
        // columns exceed it legitimately overflows and scrolls, so the last column is
        // reachable and forcing the larger total would push it off-screen.
        //
        // Pins the total of ANY fixed-layout table, not just fill/none — full-mode virtual
        // ones need it too, because a virtual table has no intrinsic width (its holder is
        // an overflow-clipped scroll container), so a `width: min-content` ancestor either
        // collapses onto its min-width floor (Firefox) or creeps 1px per frame up to the
        // column total (Chrome). Requires a definite `fitParent`, which full-mode tables
        // only get from a caller-supplied parentWidth — see useAvailableWidth.
        const fitTotal = this.state.extendedProps.tableLayout === 'fixed' && outer.computedTotalWidth > 0
          ? outer.computedTotalWidth
          : undefined;
        const fitParent = this.props.widthOptions?.parentWidth ?? this.state?.containerWidth;
        // The var sizes the body's outer box, which loses the reserved gutter to its vertical
        // scrollbar again — add it back unless the measurement found no scrollbar.
        const fitWidth = fitTotal != null && this.state.hasVerticalScroll !== false
          ? fitTotal + outer.reservedGutter
          : fitTotal;
        const applyFit = fitWidth != null && fitParent != null && fitWidth <= fitParent;
        // Never pin the element usesContainerWidth() observes — see .xm-table-fitwidth-pin.
        const fitClass = [
          wrapperClass,
          applyFit && 'xm-table-fitwidth',
          applyFit && !this.usesContainerWidth() && 'xm-table-fitwidth-pin',
        ].filter(Boolean).join(' ');
        const fitVar = applyFit
          ? ({ ['--xm-fit-width']: `${fitWidth}px` } as React.CSSProperties)
          : undefined;

        if (useVirtualization) {
          // rc-virtual needs a numeric scroll.x AND a numeric width on every column, or a
          // column falls back to an equal average share ("all columns equal") and the
          // separate header/body tables desync. Every virtual table gets the engine total,
          // not just the trim-to-fit ones covered by engineFixedScrollX.
          const virtualScrollX = engineFixedScrollX
            ?? (outer.computedTotalWidth > 0 ? outer.computedTotalWidth : undefined);
          // return <LazyLoadComponent threshold={-100} delayTime={20000} delayMethod='debounce' placeholder={<div style={{ height: 200 }}></div>}>
          //   <Delayed waitBeforeShow={2000} placeholder={<div style={{ height: 200 }}><Spinner /></div>}>
          return (
            <div ref={this.tableRef} className={fitClass} style={fitVar}>
              <VirtualTable<RecordEntry>
                size="small"
                sticky={true}
                showSorterTooltip={false}
                pagination={false}
                bordered
                {...this.state.extendedProps}
                {...(this.state.extendedProps?.noScrollY
                  ? {}
                  : virtualScrollX
                    ? { scroll: { x: virtualScrollX, y: this.state.scrollY } }
                    : { scroll: { y: this.state.scrollY } }
                )}
                horizontalVirtualization={this.props.horizontalVirtualization}
                maxVisibleColumns={this.props.maxVisibleColumns}
                loading={this.props.loading}
              />
            </div>
          );

          //   </Delayed>
          // </LazyLoadComponent>;
        }

        const plainScrollX = engineFixedScrollX
          ?? ((!this.state.hasHorizontalOverflow)
            ? undefined
            : (this.state.extendedProps?.scroll?.x ?? 'max-content'));

        // return <LazyLoadComponent threshold={-100} delayTime={20000} delayMethod='debounce' placeholder={<div style={{ height: 200 }}></div>}>
        //   <Delayed waitBeforeShow={2000} placeholder={<div style={{ height: 200 }}><Spinner /></div>}>
        return (
          <div ref={this.tableRef} className={fitClass} style={fitVar}>
            <Table<RecordEntry>
              size="small"
              showSorterTooltip={false}
              pagination={false}
              bordered
              ref={r => {
                outer.tableEl = r;
              }}
              summary={this.state.extendedProps.summaryRow ? () => <TableSummary<RecordEntry> data={this.state.extendedProps.summaryRow} cols={this.state.extendedProps.children} /> : null}
              {...this.state.extendedProps}
              {...(this.state.extendedProps?.noScrollY
                // noScrollY suppresses the Y axis ONLY. Dropping x with it left rc-table's
                // single `.ant-table-content` box without overflow-x and the table at
                // width:100%, so a too-wide table couldn't scroll — it pushed the page and
                // auto layout squeezed whichever column could shrink.
                ? (plainScrollX ? { scroll: { x: plainScrollX } } : {})
                : { scroll: { x: plainScrollX, y: this.state.scrollY } }
              )}
              columns={this.state.extendedProps.columns as ColumnsType<RecordEntry>}
              sticky={false}
              loading={this.props.loading}
            />
          </div>
        );

        //   </Delayed>
        // </LazyLoadComponent>;
      }
    };

  }

}
